/**
 * SETTINGS PAGE
 * User and system settings
 * Apple Liquid Glass Design System - WWDC25
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  User,
  Bell,
  Shield,
  Globe,
  Sun,
  Moon,
  Monitor,
  Mail,
  Key,
  Smartphone,
  Eye,
  EyeOff,
  Check,
  ChevronRight
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'

// Animation configurations
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

interface ToggleProps {
  enabled: boolean
  onChange: (value: boolean) => void
}

function Toggle({ enabled, onChange }: ToggleProps) {
  return (
    <motion.button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-7 rounded-full transition-colors ${
        enabled ? 'bg-green' : 'bg-black/10 dark:bg-white/10'
      }`}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md"
        animate={{ x: enabled ? 20 : 0 }}
        transition={springConfig}
      />
    </motion.button>
  )
}

export function SettingsPage() {
  const { user } = useAuth()
  const { theme, setTheme, actualTheme } = useTheme()

  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [pushNotifications, setPushNotifications] = useState(false)
  const [transactionAlerts, setTransactionAlerts] = useState(true)

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'auto', label: 'Auto', icon: Monitor }
  ]

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-8 space-y-8"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants}>
        <h1 className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
          Settings
        </h1>
        <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
          Manage your account and application preferences
        </p>
      </motion.div>

      <div className="max-w-4xl space-y-6">
        {/* Account Settings */}
        <motion.div variants={itemVariants}>
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue to-purple rounded-xl flex items-center justify-center shadow-sm">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Account Information
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Your personal details
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={user?.name || ''}
                    disabled
                    className="w-full px-4 py-3 bg-white/30 dark:bg-white/5 border border-black/[0.06] dark:border-white/[0.06] rounded-xl text-body text-light-text-primary dark:text-dark-text-primary disabled:opacity-60"
                  />
                  <Badge variant="blue" size="sm" className="absolute right-3 top-1/2 -translate-y-1/2">
                    Verified
                  </Badge>
                </div>
              </div>

              <div>
                <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-3 bg-white/30 dark:bg-white/5 border border-black/[0.06] dark:border-white/[0.06] rounded-xl text-body text-light-text-primary dark:text-dark-text-primary disabled:opacity-60"
                  />
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary" />
                </div>
              </div>

              <div>
                <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
                  Account Type
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={user?.type || ''}
                    disabled
                    className="w-full px-4 py-3 bg-white/30 dark:bg-white/5 border border-black/[0.06] dark:border-white/[0.06] rounded-xl text-body text-light-text-primary dark:text-dark-text-primary disabled:opacity-60"
                  />
                  <StatusBadge
                    status={user?.type === 'Admin' ? 'success' : 'info'}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {user?.type}
                  </StatusBadge>
                </div>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Theme Settings */}
        <motion.div variants={itemVariants}>
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-purple to-pink rounded-xl flex items-center justify-center shadow-sm">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Appearance
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Customize how the app looks
                </p>
              </div>
            </div>

            <div>
              <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-4">
                Theme Mode
              </label>
              <div className="grid grid-cols-3 gap-3">
                {themeOptions.map((option) => {
                  const Icon = option.icon
                  const isSelected = theme === option.value
                  return (
                    <motion.button
                      key={option.value}
                      onClick={() => setTheme(option.value as any)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-blue bg-blue/10'
                          : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/10 dark:hover:border-white/10'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          isSelected ? 'bg-blue/20' : 'bg-black/5 dark:bg-white/5'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-blue' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}`} />
                        </div>
                        <span className={`text-subheadline font-medium ${
                          isSelected ? 'text-blue' : 'text-light-text-secondary dark:text-dark-text-secondary'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-2 right-2 w-5 h-5 bg-blue rounded-full flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                    </motion.button>
                  )
                })}
              </div>
              <p className="mt-3 text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                Current theme: <span className="font-medium">{actualTheme === 'dark' ? 'Dark' : 'Light'}</span>
                {theme === 'auto' && ' (Auto mode follows system preference)'}
              </p>
            </div>
          </GlassCard>
        </motion.div>

        {/* Notifications */}
        <motion.div variants={itemVariants}>
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-orange to-red rounded-xl flex items-center justify-center shadow-sm">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Notifications
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Manage how you receive updates
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <motion.div
                whileHover={{ x: 4 }}
                className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue" />
                  </div>
                  <div>
                    <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                      Email Notifications
                    </p>
                    <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                      Receive updates via email
                    </p>
                  </div>
                </div>
                <Toggle enabled={emailNotifications} onChange={setEmailNotifications} />
              </motion.div>

              <motion.div
                whileHover={{ x: 4 }}
                className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple/10 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-purple" />
                  </div>
                  <div>
                    <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                      Push Notifications
                    </p>
                    <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                      Receive push alerts on your device
                    </p>
                  </div>
                </div>
                <Toggle enabled={pushNotifications} onChange={setPushNotifications} />
              </motion.div>

              <motion.div
                whileHover={{ x: 4 }}
                className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green/10 rounded-xl flex items-center justify-center">
                    <Bell className="w-5 h-5 text-green" />
                  </div>
                  <div>
                    <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                      Transaction Alerts
                    </p>
                    <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                      Get notified about new transactions
                    </p>
                  </div>
                </div>
                <Toggle enabled={transactionAlerts} onChange={setTransactionAlerts} />
              </motion.div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Security */}
        <motion.div variants={itemVariants}>
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-green to-blue rounded-xl flex items-center justify-center shadow-sm">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Security
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Protect your account
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.01, x: 4 }}
                whileTap={{ scale: 0.99 }}
                className="w-full flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04] text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange/10 rounded-xl flex items-center justify-center">
                    <Key className="w-5 h-5 text-orange" />
                  </div>
                  <div>
                    <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                      Change Password
                    </p>
                    <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                      Update your password regularly
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.01, x: 4 }}
                whileTap={{ scale: 0.99 }}
                className="w-full flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04] text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-blue" />
                  </div>
                  <div>
                    <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                      Two-Factor Authentication
                    </p>
                    <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                      Add an extra layer of security
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="green" size="sm">Enabled</Badge>
                  <ChevronRight className="w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary" />
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.01, x: 4 }}
                whileTap={{ scale: 0.99 }}
                className="w-full flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04] text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple/10 rounded-xl flex items-center justify-center">
                    <Eye className="w-5 h-5 text-purple" />
                  </div>
                  <div>
                    <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                      Login Activity
                    </p>
                    <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                      View recent login sessions
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.button>
            </div>
          </GlassCard>
        </motion.div>

        {/* Danger Zone */}
        <motion.div variants={itemVariants}>
          <ContentCard className="border border-red/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red/10 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-red" />
              </div>
              <div>
                <h2 className="text-headline font-semibold text-red">
                  Danger Zone
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Irreversible actions
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-red/5 rounded-xl border border-red/10">
              <div>
                <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                  Delete Account
                </p>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  Permanently delete your account and all data
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="text-red border-red/20 hover:bg-red/10"
              >
                Delete
              </Button>
            </div>
          </ContentCard>
        </motion.div>
      </div>
    </motion.div>
  )
}
